import { getUrl } from '../../config';
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";

import { green } from "@mui/material/colors";

import { Chip, Drawer as MuiDrawer, ListItemButton, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ReactComponent as Logo } from "../../vendor/logo.svg";
import { ReactComponent as BlancClavier } from "../../vendor/Blanc-Clavier.svg";
import { ReactComponent as BlancJLS } from "../../vendor/Blanc-JLS.svg";
import { ReactComponent as BlancCybax } from "../../vendor/Blanc-Cybax.svg";
import { ReactComponent as BlancConfort } from "../../vendor/Blanc-Confort.svg";
import { ReactComponent as BlancH2C } from "../../vendor/Blanc-H2C.svg";
import { ReactComponent as BlancMenuiserie } from "../../vendor/Blanc-Menuiserie.svg";
import { ReactComponent as BlancInkland } from "../../vendor/Blanc-Inkland.svg";

import Footer from "./SidebarFooter";
import SidebarNav from "./SidebarNav";

const Drawer = styled(MuiDrawer)`
  border-right: 0;
  overflow-x: hidden;

  .MuiDrawer-paper {
    border-right: none;
    box-shadow: 6px 0 24px rgba(0, 0, 0, 0.15);
    background-color: ${(props) => props.theme.sidebar.background};
    background-image: linear-gradient(180deg, #1f2937 0%, #111827 100%);
  }

  > div {
    border-right: 0;
    overflow-x: hidden;
  }
`;

const Brand = styled.div`
  min-height: 135px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
  overflow: hidden;
  max-width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 135px;
  }
`;

const BrandIcon = styled.div`
  max-width: 100%;
  width: 100%;
  height: 115px;
  margin-top: 10px;
  margin-bottom: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }

  svg {
    width: 100%;
    height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`;

const BrandChip = styled(Chip)`
  background-color: ${green[700]};
  border-radius: 5px;
  color: ${(props) => props.theme.palette.common.white};
  font-size: 55%;
  height: 18px;
  margin-left: 2px;
  margin-top: -16px;
  padding: 3px 0;

  span {
    padding-left: ${(props) => props.theme.spacing(1.375)};
    padding-right: ${(props) => props.theme.spacing(1.375)};
  }
`;

const Sidebar = ({ items, showFooter = true, ...rest }) => {
  const [currentLogo, setCurrentLogo] = useState(Logo);

  const getLogoByErpId = (erpId) => {
    switch (parseInt(erpId)) {
      case 1:
        return BlancInkland;
      case 2:
        return BlancClavier;
      case 3:
        return BlancJLS;
      case 4:
        return BlancCybax;
      case 5:
        return Logo;
      case 6:
        return BlancConfort;
      case 7:
        return BlancH2C;
      case 8:
        return BlancMenuiserie;
      default:
        return Logo;
    }
  };

  const updateLogo = () => {
    const erpId = window.localStorage.getItem("erpId");
    setCurrentLogo(getLogoByErpId(erpId));
  };

  useEffect(() => {
    updateLogo();
    
    // Écouter les changements de localStorage
    const handleStorageChange = (e) => {
      if (e.key === "erpId") {
        updateLogo();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Écouter les changements d'ERP via le menu (custom event)
    const handleErpChange = () => {
      updateLogo();
    };

    window.addEventListener('erpChanged', handleErpChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('erpChanged', handleErpChange);
    };
  }, []);

  const LogoComponent = currentLogo;

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand>
        {/* Bouton de fermeture pour le menu mobile (drawer temporaire) */}
        {rest.variant === "temporary" && typeof rest.onClose === "function" && (
          <IconButton
            size="small"
            onClick={rest.onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "common.white",
              bgcolor: "rgba(0,0,0,0.25)",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.4)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        <BrandIcon>
          <LogoComponent />
        </BrandIcon>
      </Brand>
      <SidebarNav items={items} />
      {!!showFooter && <Footer />}
    </Drawer>
  );
};

export default Sidebar;
